/**
 * 从 MP4 文件解析视频元数据（宽高、时长、编码）
 *
 * 算法来源：scripts/api-explorer/shipinhao-video-upload/mp4-parse-poc.mjs
 * 已通过合成样本 8/8 + 真实 Chrome 编码视频交叉验证（见该 PoC）。
 *
 * 只读 ISO BMFF 的 box 头做定位，元数据 box（moov / moof）才单独读进内存，
 * 媒体数据（mdat）直接跳过 —— 实测 78MB 视频只读 14.64KB（占全文 0.018%）。
 * 因此内存占用与文件大小无关，也不受 fs.readFileSync 的 2GiB 上限约束
 * （早先的实现整文件载入，2GB 以上必然抛 RangeError 并被误报成「格式不支持」）。
 * 支持 MP4（ISO base media file format），不支持 AVI/MKV/FLV。
 *
 * 时长按四级回退取值：mvhd → 视频轨 mdhd → stts 累加 → moof/trun 累加。
 * 分片 MP4（fMP4，moov 里带 mvex/trex）的 mvhd/mdhd/stts 全为 0，
 * 真实时长只存在于各 moof 分片中，必须走最后一级才能拿到。
 */

import fs from "node:fs";

export interface VideoMeta {
    width: number;
    height: number;
    /** 时长（秒，浮点数） */
    duration: number;
    /**
     * 旋转角度（0/90/180/270），手机竖屏视频常为 90
     *
     * 仅用于把 tkhd 里的横屏尺寸换算成显示尺寸，不写进发布体。
     * 由 buildVideoMetaFromParams 组装时恒为 0：浏览器给出的 videoWidth/videoHeight
     * 已经是应用过旋转的显示尺寸，再换算一次会把宽高交换错。
     */
    rotation: number;
    fileSize: number;
    /**
     * 视频轨编码格式（stsd 里的 sample entry fourcc，小写）
     * H.264 为 avc1/avc3，H.265 为 hvc1/hev1，解析不到时为 null
     */
    codec: string | null;
}

/**
 * 单个元数据 box（moov / moof）的读取上限
 *
 * 8 小时 30fps 的视频，moov 里 stsz/stco 样本表约几十 MB，256MB 足够宽松。
 * 设上限只为防畸形文件声明出荒谬的 box size 导致大额分配。
 */
const MAX_METADATA_BOX_SIZE = 256 * 1024 * 1024;

/** 解析过程中累积的轨道信息 */
interface TrackInfo {
    trackId: number;
    width: number;
    height: number;
    rotation: number;
    handler: string | null;
    codec: string | null;
    /** 该轨自己的时间刻度（mdhd.timescale），fMP4 里算分片时长要用它 */
    timescale: number;
    /** mdhd.duration，fMP4 为 0 */
    mdhdDuration: number;
    /** stts 累加的样本总时长，fMP4 里 stts 为空表故为 0 */
    sttsDuration: number;
}

/** 一次解析的可变状态，跨多个 moov/moof 分片累积 */
interface ParseState {
    movie: { timescale: number; duration: number } | null;
    tracks: TrackInfo[];
    /** trackId → trex.default_sample_duration，trun 未逐样本给时长时用它兜底 */
    trexDefaults: Map<number, number>;
    /** trackId → 各 moof 分片累加的样本时长（该轨 timescale 下） */
    fragmentDurations: Map<number, number>;
    /** 当前 traf 的上下文，tfhd 写入、紧随其后的 trun 读取 */
    trafTrackId: number;
    trafDefaultSampleDuration: number;
}

/**
 * 读取一个 box 头，返回类型、总长与头部长度
 *
 * 头部最长 16 字节（size===1 的 64 位 largesize）。返回 null 表示读不到完整头。
 */
function readBoxHeader(
    fd: number,
    offset: number,
    limit: number,
): { type: string; size: number; headerSize: number } | null {
    if (offset + 8 > limit) return null;

    const head = Buffer.alloc(16);
    const read = fs.readSync(fd, head, 0, 16, offset);
    if (read < 8) return null;

    let size = head.readUInt32BE(0);
    const type = head.toString("latin1", 4, 8);
    let headerSize = 8;

    if (size === 1) {
        if (read < 16) return null;
        size = head.readUInt32BE(8) * 2 ** 32 + head.readUInt32BE(12);
        headerSize = 16;
    } else if (size === 0) {
        // 「延伸到文件末尾」
        size = limit - offset;
    }

    if (size < headerSize || offset + size > limit) return null;

    return { type, size, headerSize };
}

export function parseVideoMeta(filePath: string): VideoMeta | null {
    let fd: number;
    let fileSize: number;
    try {
        fd = fs.openSync(filePath, "r");
    } catch {
        return null;
    }

    try {
        fileSize = fs.fstatSync(fd).size;

        const state: ParseState = {
            movie: null,
            tracks: [],
            trexDefaults: new Map(),
            fragmentDurations: new Map(),
            trafTrackId: 0,
            trafDefaultSampleDuration: 0,
        };

        // 扫顶层 box：只把 moov / moof 读进内存，mdat 等纯数据 box 靠 size 跳过
        let offset = 0;
        while (offset + 8 <= fileSize) {
            const header = readBoxHeader(fd, offset, fileSize);
            if (!header) break;

            if (header.type === "moov" || header.type === "moof") {
                const bodyLength = header.size - header.headerSize;
                if (bodyLength > 0 && bodyLength <= MAX_METADATA_BOX_SIZE) {
                    const body = Buffer.alloc(bodyLength);
                    const read = fs.readSync(fd, body, 0, bodyLength, offset + header.headerSize);
                    // moof 的 traf 上下文不跨分片复用，进入前先清掉
                    state.trafTrackId = 0;
                    state.trafDefaultSampleDuration = 0;
                    walkBoxes(body.subarray(0, read), 0, read, state);
                }
            }

            offset += header.size;
        }

        const { movie, tracks } = state;
        const video = tracks.find((t) => t.handler === "vide") || tracks.find((t) => t.width > 0 && t.height > 0);
        if (!video) return null;

        let { width, height } = video;
        // 竖屏视频：tkhd 存横屏尺寸，靠变换矩阵判 90/270° 并交换宽高
        if (video.rotation === 90 || video.rotation === 270) {
            [width, height] = [height, width];
        }

        return {
            width: Math.round(width),
            height: Math.round(height),
            duration: resolveDuration(movie, video, state.fragmentDurations),
            rotation: video.rotation,
            fileSize,
            codec: video.codec,
        };
    } catch {
        return null;
    } finally {
        try {
            fs.closeSync(fd);
        } catch {
            // 关闭失败无补救手段，忽略
        }
    }
}

/**
 * 递归遍历已载入内存的 box 树（moov / moof 的内容）
 *
 * 与顶层扫描分开：这里的 buf 只含元数据，可以放心随机访问。
 */
function walkBoxes(buf: Buffer, start: number, end: number, state: ParseState): void {
    let off = start;

    while (off + 8 <= end) {
        let size = buf.readUInt32BE(off);
        const type = buf.toString("latin1", off + 4, off + 8);
        let headerSize = 8;

        if (size === 1) {
            // 64-bit largesize
            if (off + 16 > end) break;
            const hi = buf.readUInt32BE(off + 8);
            const lo = buf.readUInt32BE(off + 12);
            size = hi * 2 ** 32 + lo;
            headerSize = 16;
        } else if (size === 0) {
            size = end - off;
        }

        if (size < headerSize || off + size > end) break;

        const bodyStart = off + headerSize;
        const bodyEnd = off + size;

        switch (type) {
            case "trak":
            case "mdia":
            case "minf":
            case "stbl":
            case "mvex":
            case "traf":
                walkBoxes(buf, bodyStart, bodyEnd, state);
                break;
            case "mvhd":
                state.movie = parseMvhd(buf, bodyStart, bodyEnd);
                break;
            case "tkhd":
                state.tracks.push({
                    ...parseTkhd(buf, bodyStart, bodyEnd),
                    handler: null,
                    codec: null,
                    timescale: 0,
                    mdhdDuration: 0,
                    sttsDuration: 0,
                });
                break;
            case "mdhd": {
                // mdhd 与 tkhd 同属一个 trak，解析顺序上 tkhd 必先入栈
                const mdhd = parseMvhd(buf, bodyStart, bodyEnd);
                if (mdhd && state.tracks.length) {
                    const track = state.tracks[state.tracks.length - 1];
                    track.timescale = mdhd.timescale;
                    track.mdhdDuration = mdhd.duration;
                }
                break;
            }
            case "hdlr":
                if (bodyEnd - bodyStart >= 12 && state.tracks.length) {
                    state.tracks[state.tracks.length - 1].handler = buf.toString(
                        "latin1",
                        bodyStart + 8,
                        bodyStart + 12,
                    );
                }
                break;
            case "stsd": {
                // version/flags(4) + entry_count(4)，随后每个 entry 为 size(4) + format fourcc(4)
                const codec = parseStsdCodec(buf, bodyStart, bodyEnd);
                if (codec && state.tracks.length) state.tracks[state.tracks.length - 1].codec = codec;
                break;
            }
            case "stts":
                if (state.tracks.length) {
                    state.tracks[state.tracks.length - 1].sttsDuration = parseSttsDuration(buf, bodyStart, bodyEnd);
                }
                break;
            case "trex": {
                // version/flags(4) + track_ID(4) + default_sample_description_index(4)
                // + default_sample_duration(4) + default_sample_size(4) + default_sample_flags(4)
                if (bodyStart + 20 > bodyEnd) break;
                state.trexDefaults.set(buf.readUInt32BE(bodyStart + 4), buf.readUInt32BE(bodyStart + 12));
                break;
            }
            case "tfhd": {
                const tfhd = parseTfhd(buf, bodyStart, bodyEnd);
                state.trafTrackId = tfhd.trackId;
                state.trafDefaultSampleDuration =
                    tfhd.defaultSampleDuration || state.trexDefaults.get(tfhd.trackId) || 0;
                break;
            }
            case "trun": {
                const duration = parseTrunDuration(buf, bodyStart, bodyEnd, state.trafDefaultSampleDuration);
                state.fragmentDurations.set(
                    state.trafTrackId,
                    (state.fragmentDurations.get(state.trafTrackId) || 0) + duration,
                );
                break;
            }
        }

        off += size;
    }
}

/** ISO BMFF 里表示「时长未知」的哨兵值，直播录制/未正常结束的文件常见 */
const UNKNOWN_DURATION_32 = 0xffffffff;

/** 时长是否可用：0 表示缺失，0xFFFFFFFF 是「未知」哨兵，都要继续回退 */
function isUsableDuration(duration: number): boolean {
    return duration > 0 && duration !== UNKNOWN_DURATION_32;
}

/**
 * 四级回退取时长（秒）
 *
 * 1. mvhd：普通 MP4 走到这一步就够了
 * 2. 视频轨 mdhd：mvhd 缺失/为 0 时的常规兜底
 * 3. stts 样本累加：mdhd.duration 为 0 但样本表完整的文件
 * 4. moof/trun 分片累加：fMP4 唯一带时长的地方（moov 里前三项全为 0）
 *
 * 任一级算出可用值即采用，全都拿不到时返回 0（交服务端判断）。
 * 0xFFFFFFFF 视为「未知」而非真实时长 —— 否则会算出 49.7 天并被时长上限误拦。
 */
function resolveDuration(
    movie: { timescale: number; duration: number } | null,
    video: { trackId: number; timescale: number; mdhdDuration: number; sttsDuration: number },
    fragmentDurations: Map<number, number>,
): number {
    if (movie && movie.timescale && isUsableDuration(movie.duration)) {
        return movie.duration / movie.timescale;
    }

    if (video.timescale) {
        if (isUsableDuration(video.mdhdDuration)) return video.mdhdDuration / video.timescale;
        if (video.sttsDuration > 0) return video.sttsDuration / video.timescale;

        // fMP4：tfhd 的 track_ID 与 tkhd 一致，取不到时退回唯一分片（单轨文件的常见情形）
        const fragment =
            fragmentDurations.get(video.trackId) ??
            (fragmentDurations.size === 1 ? [...fragmentDurations.values()][0] : 0);
        if (fragment > 0) return fragment / video.timescale;
    }

    return 0;
}

/** stts 累加样本时长（该轨 timescale 下），空表返回 0 */
function parseSttsDuration(buf: Buffer, start: number, end: number): number {
    // version/flags(4) + entry_count(4) + [sample_count(4), sample_delta(4)]*N
    if (start + 8 > end) return 0;
    const entryCount = buf.readUInt32BE(start + 4);
    let total = 0;
    for (let i = 0; i < entryCount; i++) {
        const off = start + 8 + i * 8;
        if (off + 8 > end) break;
        total += buf.readUInt32BE(off) * buf.readUInt32BE(off + 4);
    }
    return total;
}

/** tfhd：取 track_ID 与可选的 default_sample_duration（flags bit 0x000008） */
function parseTfhd(buf: Buffer, start: number, end: number): { trackId: number; defaultSampleDuration: number } {
    // version(1) + flags(3) + track_ID(4)，之后按 flags 依次出现可选字段
    if (start + 8 > end) return { trackId: 0, defaultSampleDuration: 0 };

    const flags = buf.readUIntBE(start + 1, 3);
    const trackId = buf.readUInt32BE(start + 4);
    let off = start + 8;

    if (flags & 0x000001) off += 8; // base_data_offset
    if (flags & 0x000002) off += 4; // sample_description_index

    if (flags & 0x000008 && off + 4 <= end) {
        return { trackId, defaultSampleDuration: buf.readUInt32BE(off) };
    }

    return { trackId, defaultSampleDuration: 0 };
}

/**
 * trun 累加本分片样本时长
 *
 * 优先用逐样本的 sample_duration（flags bit 0x000100）；未给出时按
 * sample_count × defaultSampleDuration（来自 tfhd 或 trex）估算。
 */
function parseTrunDuration(buf: Buffer, start: number, end: number, defaultSampleDuration: number): number {
    // version(1) + flags(3) + sample_count(4)，之后按 flags 出现可选字段与样本表
    if (start + 8 > end) return 0;

    const flags = buf.readUIntBE(start + 1, 3);
    const sampleCount = buf.readUInt32BE(start + 4);
    let off = start + 8;

    if (flags & 0x000001) off += 4; // data_offset
    if (flags & 0x000004) off += 4; // first_sample_flags

    const hasDuration = (flags & 0x000100) !== 0;
    if (!hasDuration) return sampleCount * defaultSampleDuration;

    // 每个样本按 flags 决定字段组合，sample_duration 恒为首个
    const entrySize =
        4 +
        ((flags & 0x000200) !== 0 ? 4 : 0) + // sample_size
        ((flags & 0x000400) !== 0 ? 4 : 0) + // sample_flags
        ((flags & 0x000800) !== 0 ? 4 : 0); // sample_composition_time_offset

    let total = 0;
    for (let i = 0; i < sampleCount; i++) {
        const entryOff = off + i * entrySize;
        if (entryOff + 4 > end) break;
        total += buf.readUInt32BE(entryOff);
    }
    return total;
}

/** 读 stsd 第一个 sample entry 的 fourcc（如 avc1/hvc1），失败返回 null */
function parseStsdCodec(buf: Buffer, start: number, end: number): string | null {
    // version/flags(4) + entry_count(4) + entry size(4) + fourcc(4)
    if (start + 16 > end) return null;
    // entry_count 为 0 时后面没有 sample entry，不能把下一个 box 头当成 fourcc 读
    if (buf.readUInt32BE(start + 4) === 0) return null;
    return buf.toString("latin1", start + 12, start + 16).toLowerCase();
}

function parseMvhd(buf: Buffer, start: number, end: number): { timescale: number; duration: number } | null {
    const version = buf.readUInt8(start);
    if (version === 1) {
        if (start + 28 > end) return null;
        const timescale = buf.readUInt32BE(start + 20);
        const hi = buf.readUInt32BE(start + 24);
        const lo = buf.readUInt32BE(start + 28);
        return { timescale, duration: hi * 2 ** 32 + lo };
    }
    if (start + 20 > end) return null;
    return { timescale: buf.readUInt32BE(start + 12), duration: buf.readUInt32BE(start + 16) };
}

function parseTkhd(
    buf: Buffer,
    start: number,
    end: number,
): { trackId: number; width: number; height: number; rotation: number } {
    const version = buf.readUInt8(start);
    const afterDuration = version === 1 ? 36 : 24;
    const matrixOff = start + afterDuration + 16;
    const whOff = matrixOff + 36;

    // track_ID 紧跟创建/修改时间：v0 偏移 12，v1 偏移 20（时间戳为 64 位）
    const trackIdOff = start + (version === 1 ? 20 : 12);
    const trackId = trackIdOff + 4 <= end ? buf.readUInt32BE(trackIdOff) : 0;

    if (whOff + 8 > end) return { trackId, width: 0, height: 0, rotation: 0 };

    // 16.16 定点数
    const width = buf.readUInt32BE(whOff) / 65536;
    const height = buf.readUInt32BE(whOff + 4) / 65536;

    // 变换矩阵前两个元素判旋转
    const a = buf.readInt32BE(matrixOff) / 65536;
    const b = buf.readInt32BE(matrixOff + 4) / 65536;
    let rotation = 0;
    if (Math.abs(a) < 0.01 && Math.abs(b - 1) < 0.01) rotation = 90;
    else if (Math.abs(a + 1) < 0.01 && Math.abs(b) < 0.01) rotation = 180;
    else if (Math.abs(a) < 0.01 && Math.abs(b + 1) < 0.01) rotation = 270;

    return { trackId, width, height, rotation };
}

/** 调用方传入的视频元数据（浏览器 <video> + File 侧采集） */
export interface VideoMetadataInput {
    duration: number;
    width: number;
    height: number;
    fileSize: number;
}

/**
 * 只读视频轨编码格式，不解析其余字段
 *
 * 供「宽高时长由调用方给出，仅编码需要本地确认」的场景使用。
 * 浏览器没有暴露编码格式的 API（canPlayType/MediaCapabilities 只能回答
 * 「给定 MIME 能否播放」，无法反查文件实际编码），所以这一项只能读文件。
 * 走的是同一套分段读，成本是几 KB。
 *
 * @returns fourcc（小写，如 avc1/hvc1），读不到时 null
 */
export function parseVideoCodec(filePath: string): string | null {
    return parseVideoMeta(filePath)?.codec ?? null;
}

/**
 * 用调用方传入的元数据组装 VideoMeta，编码格式仍从文件读取
 *
 * 宽高时长直接采用传入值：浏览器的 videoWidth/videoHeight 是**已应用旋转**的显示尺寸，
 * 正是发布体需要的值，所以 rotation 固定为 0，不再做二次换算（换算会把竖屏宽高交换错）。
 *
 * codec 读取失败不影响发布 —— validateShipinhaoVideo 对 codec=null 会跳过编码校验，
 * 退化为由服务端判断，与文件解析不出编码时的既有行为一致。
 */
export function buildVideoMetaFromParams(filePath: string, metadata: VideoMetadataInput): VideoMeta {
    return {
        width: Math.round(metadata.width),
        height: Math.round(metadata.height),
        duration: metadata.duration,
        rotation: 0,
        fileSize: metadata.fileSize,
        codec: parseVideoCodec(filePath),
    };
}
